import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(response.data);
            setIsAuthenticated(true);
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const response = await axios.post(
            `${API}/auth/login`,
            { email, password },
            { withCredentials: true }
        );
        setUser(response.data);
        setIsAuthenticated(true);
        return response.data;
    };

    const register = async (userData) => {
        const response = await axios.post(
            `${API}/auth/register`,
            userData,
            { withCredentials: true }
        );
        setUser(response.data);
        setIsAuthenticated(true);
        return response.data;
    };

    const processOAuthSession = async (sessionId) => {
        const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
        );
        setUser(response.data);
        setIsAuthenticated(true);
        return response.data;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        }
        setUser(null);
        setIsAuthenticated(false);
    };

    const initiateGoogleLogin = (redirectPath = '/dashboard') => {
        // Use the configured backend URL (REACT_APP_BACKEND_URL) as the OAuth origin so the
        // redirect_uri deterministically matches the URI registered in Google Console across
        // www/apex hosts, instead of whatever host the browser is currently on.
        const origin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const params = new URLSearchParams({
            redirect_origin: origin,
            redirect_path: redirectPath
        });
        window.location.href = `${API}/auth/google/login?${params.toString()}`;
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated,
            login,
            register,
            logout,
            checkAuth,
            processOAuthSession,
            initiateGoogleLogin
        }}>
            {children}
        </AuthContext.Provider>
    );
};
